import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ScrollView,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { Popable } from "react-native-popable";
import HiringTemplatesAPI from "../../../../../API/HiringTemplates";
import ToothFairyButton from "../../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyLoadingPlaceholder from "../../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import ToothFairyModal from "../../../../../Components/ToothFairyComponents/ToothFairyModal";
import useWorkspaces from "../../../../../Hooks/useWorkspaces";
import { isMobileScreen } from "../../../../MainViewContainer";
import WorkspaceChip from "../../../../Workspaces/Components/WorkspaceChip";

const promptIcon = AppAssets.icons.writer;

const getGroupInitials = (groupName) => {
  const cleaned = (groupName || "")
    .replace(/\b(prompts?|tools?|connectors?|templates?|pack|suite)\b/gi, " ")
    .trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  const initials = words
    .slice(0, 2)
    .map((w) => w[0])
    .join("");
  return (initials || (groupName || "?").slice(0, 2)).toUpperCase();
};

const GroupAvatar = ({ groupName, currentTheme, size = 34 }) => {
  const backgroundColor = AppUtilities.stringToHslColor(
    groupName || "prompts",
    60
  );
  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        backgroundColor,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <AppText
        style={{ fontSize: size * 0.38, fontWeight: "bold" }}
        color={currentTheme?.theme?.white}
        preventTranslation
      >
        {getGroupInitials(groupName)}
      </AppText>
    </View>
  );
};

const CreateNewCard = ({ onPress, currentTheme, t }) => {
  const theme = currentTheme?.theme;
  return (
    <TouchableOpacity
      onPress={onPress}
      style={{ marginRight: 10, marginTop: 10 }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          width: 140,
          paddingHorizontal: 12,
          paddingVertical: 10,
          borderRadius: 10,
          borderWidth: 1,
          borderColor: theme?.app_outline_color,
          backgroundColor: theme?.softBackgroundColor,
        }}
      >
        <AppIcon icon={promptIcon} size={15} color={theme?.primary} />
        <AppText
          style={{ fontSize: 13, fontWeight: "600", marginLeft: 8 }}
          numberOfLines={1}
        >
          {t("settings.prompt.promptLabel", "Prompt")}
        </AppText>
      </View>
    </TouchableOpacity>
  );
};

const PromptCard = ({ prompt, onPress, currentTheme, isSelected, t }) => {
  const theme = currentTheme?.theme;
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityLabel={`prompt-card-${prompt.id}`}
    >
      <View
        style={{
          width: 320,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: isSelected ? theme?.primary : theme?.app_outline_color,
          backgroundColor: theme?.softBackgroundColor,
          padding: 16,
          marginLeft: 20,
          marginBottom: 16,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <View
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              backgroundColor: theme?.purple,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AppIcon icon={promptIcon} size={16} color={theme?.white} />
          </View>
          <View style={{ flex: 1, marginLeft: 12 }}>
            <AppText
              style={{ fontSize: 15, fontWeight: "600" }}
              numberOfLines={1}
              preventTranslation
            >
              {prompt?.name}
            </AppText>
            {!!prompt?.groupName && (
              <AppText
                style={{ fontSize: 11, fontWeight: "bold", marginTop: 2 }}
                color={theme?.purple}
                numberOfLines={1}
                preventTranslation
              >
                {prompt?.groupName}
              </AppText>
            )}
          </View>
          {!!prompt?.description && (
            <Popable
              numberOfLines={10}
              backgroundColor={theme?.tooltipBackgroundColor}
              style={{ maxWidth: 260, width: 260, zIndex: 20 }}
              content={prompt.description}
              action="hover"
              position="bottom"
            >
              <AppIcon
                icon={AppAssets.icons.info}
                size={14}
                color={theme?.mediumGrey}
              />
            </Popable>
          )}
        </View>
        <AppText
          style={{
            fontSize: 12,
            lineHeight: 17,
            color: theme?.darkGrey,
            marginTop: 10,
            minHeight: 34,
          }}
          numberOfLines={2}
          preventTranslation
        >
          {prompt?.description || t("settings.list.noDescriptionProvided")}
        </AppText>
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 6,
            marginTop: 12,
          }}
        >
          <WorkspaceChip
            label={t("settings.prompt.promptLabel", "Prompt")}
            leftIcon={promptIcon}
            backgroundColor={theme?.purple}
            fontColor={theme?.white}
          />
        </View>
      </View>
    </TouchableOpacity>
  );
};

const PromptPreviewPanel = ({ prompt, currentTheme, t, onImport }) => {
  const theme = currentTheme?.theme;
  return (
    <View
      style={{
        width: 360,
        borderLeftWidth: 1,
        borderLeftColor: theme?.lightGrey,
        flexDirection: "column",
        flexShrink: 0,
        height: "100%",
        overflow: "hidden",
      }}
    >
      {!prompt ? (
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            padding: 30,
          }}
        >
          <AppPlaceholderView
            wrapperStyle={{
              width: "80%",
              height: "100%",
              marginVertical: "auto",
              marginHorizontal: "auto",
              alignItems: "center",
            }}
            mainCaption={t(
              "settings.prompt.noPromptSelected",
              "No prompt selected"
            )}
            subCaption={t(
              "settings.prompt.noPromptSelectedDesc",
              "Select a prompt from the list to preview its details and import options"
            )}
            size={30}
            icon={promptIcon}
          />
        </View>
      ) : (
        <>
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ padding: 24, paddingBottom: 12 }}
            showsVerticalScrollIndicator
          >
            <AppText
              style={{
                fontSize: 11,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: theme?.mediumGrey,
              }}
            >
              {t("settings.qa.preview")}
            </AppText>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginTop: 10,
              }}
            >
              <GroupAvatar
                groupName={prompt?.groupName}
                currentTheme={currentTheme}
              />
              <AppText
                style={{
                  fontSize: 20,
                  fontWeight: "600",
                  marginLeft: 12,
                  flex: 1,
                }}
                preventTranslation
              >
                {prompt?.name}
              </AppText>
            </View>
            {!!prompt?.groupName && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginTop: 8,
                }}
              >
                <AppText
                  style={{
                    fontSize: 11,
                    fontWeight: "bold",
                    color: theme?.purple,
                  }}
                  preventTranslation
                >
                  {prompt?.groupName}
                </AppText>
              </View>
            )}
            {!!prompt?.description && (
              <AppText
                style={{
                  fontSize: 13,
                  lineHeight: 20,
                  color: theme?.darkGrey,
                  marginTop: 16,
                }}
                preventTranslation
              >
                {prompt?.description}
              </AppText>
            )}
            <AppText
              style={{
                fontSize: 11,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: theme?.mediumGrey,
                marginTop: 24,
                marginBottom: 10,
              }}
            >
              {t("settings.qa.capabilitiesLabel")}
            </AppText>
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 6,
              }}
            >
              <WorkspaceChip
                label={t("settings.prompt.promptLabel", "Prompt")}
                leftIcon={promptIcon}
                backgroundColor={theme?.purple}
                fontColor={theme?.white}
              />
            </View>
          </ScrollView>
          <View
            style={{
              padding: 20,
              borderTopWidth: 1,
              borderTopColor: theme?.lightGrey,
            }}
          >
            <ToothFairyButton
              title={t(
                "settings.prompt.importThisPrompt",
                "Import this prompt"
              )}
              onPress={() => onImport(prompt)}
              wrapperStyle={{ width: "100%" }}
              style={{ marginHorizontal: 0, height: 38 }}
            />
          </View>
        </>
      )}
    </View>
  );
};

const PromptPreSelection = ({
  onNewPromptSelect,
  onTemplatePromptSelect,
  onClose,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const { state, getActiveWorkspace } = useWorkspaces();

  const [flatPrompts, setFlatPrompts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchName, setSearchName] = useState("");
  const [activeArea, setActiveArea] = useState("all");
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const showPreview = width >= 1100;
  const isFirstRenderRef = useRef(true);

  const isAuGovUser = useMemo(() => {
    const rootUserId = getActiveWorkspace()?.userID;
    const rootUser = state?.workspaceUsers?.find((u) => u?.id === rootUserId);
    return rootUser?.user_email?.toLowerCase()?.endsWith(".gov.au") || false;
  }, [state?.workspaceUsers, getActiveWorkspace()?.userID]);

  const filteredFlatPrompts = useMemo(() => {
    if (isAuGovUser) return flatPrompts;
    return flatPrompts.filter((prompt) => !prompt?.isAuGov);
  }, [flatPrompts, isAuGovUser]);

  useEffect(() => {
    loadPromptsFromTemplates();
  }, []);

  const loadPromptsFromTemplates = async () => {
    try {
      setIsLoading(true);
      const groups =
        await HiringTemplatesAPI.fetchHireablePromptsFromTemplates();

      const prompts = [];
      Object.entries(groups).forEach(([groupName, groupData]) => {
        (groupData.prompts || []).forEach((prompt) => {
          prompts.push({
            ...prompt,
            groupName,
          });
        });
      });

      setFlatPrompts(prompts);
    } catch (error) {
      console.error("Failed to load prompts from templates:", error);
      setFlatPrompts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const areas = useMemo(() => {
    const names = [
      ...new Set(filteredFlatPrompts.map((p) => p?.groupName).filter(Boolean)),
    ];
    return names.sort((a, b) => a.localeCompare(b));
  }, [filteredFlatPrompts]);

  const visiblePrompts = useMemo(() => {
    const needle = searchName?.toLowerCase();
    return filteredFlatPrompts.filter((prompt) => {
      const areaMatch =
        activeArea === "all" || prompt?.groupName === activeArea;
      if (!areaMatch) return false;
      if (!needle) return true;
      return (
        prompt?.name?.toLowerCase().includes(needle) ||
        prompt?.description?.toLowerCase().includes(needle) ||
        (prompt?.groupName || "")?.toLowerCase().includes(needle)
      );
    });
  }, [filteredFlatPrompts, activeArea, searchName]);

  const sortedPrompts = useMemo(() => {
    return [...visiblePrompts].sort((a, b) =>
      (a?.name || "").localeCompare(b?.name || "")
    );
  }, [visiblePrompts]);

  const selectedPrompt = useMemo(
    () => visiblePrompts?.find((p) => p?.id === selectedPromptId) || null,
    [visiblePrompts, selectedPromptId]
  );

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    if (
      selectedPromptId &&
      !visiblePrompts.some((prompt) => prompt?.id === selectedPromptId)
    ) {
      setSelectedPromptId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePrompts]);

  const handleCardPress = (prompt) => {
    if (showPreview) {
      setSelectedPromptId(prompt?.id);
    } else {
      onTemplatePromptSelect(prompt);
    }
  };

  return (
    <ToothFairyModal
      modalStyle={{
        minHeight: 500,
        maxWidth: 1600,
        width: isMobileScreen() ? "99%" : "95%",
        height: "94%",
        marginHorizontal: "auto",
        marginTop: "auto",
        marginBottom: "auto",
        margin: "auto",
      }}
      key="prompt-preselection"
      isModalOpen={true}
      setIsModalOpen={onClose}
      modalTitle={t("settings.prompt.selectPromptTemplate")}
      closeModal={onClose}
    >
      {isLoading ? (
        <ToothFairyLoadingPlaceholder caption="messages.loadingPrompts" />
      ) : (
        <View
          style={{
            flexDirection: "row",
            flex: 1,
            minHeight: 0,
          }}
        >
          <View
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              flexDirection: "column",
            }}
          >
            <View
              style={{
                padding: 24,
                paddingBottom: 16,
                borderBottomWidth: 1,
                borderBottomColor: currentTheme?.theme?.lightGrey,
              }}
            >
              <AppText
                style={{
                  fontSize: 22,
                  fontWeight: "600",
                  color: currentTheme?.theme?.font_color,
                }}
              >
                {t("settings.list.createPrompt")}
              </AppText>
              <AppText
                style={{
                  fontSize: 13,
                  color: currentTheme?.theme?.darkGrey,
                  marginTop: 4,
                  lineHeight: 19,
                }}
              >
                {t(
                  "settings.prompt.createPromptSub",
                  "Create a custom prompt or import a pre-built template"
                )}
              </AppText>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginTop: 16,
                  height: 48,
                  borderRadius: 10,
                  borderWidth: 1,
                  borderColor: currentTheme?.theme?.border_color,
                  backgroundColor: currentTheme?.theme?.inputBackgroundColor,
                  paddingLeft: 14,
                  paddingRight: 6,
                }}
              >
                <AppIcon
                  icon={AppAssets.icons.search}
                  size={16}
                  color={currentTheme?.theme?.grey}
                />
                <TextInput
                  value={searchName}
                  onChangeText={setSearchName}
                  placeholder={t("settings.prompt.searchByName")}
                  placeholderTextColor={
                    currentTheme?.theme?.inputPlaceholderColor
                  }
                  style={{
                    flex: 1,
                    height: "100%",
                    marginLeft: 10,
                    fontSize: 15,
                    fontFamily: "normalFont",
                    color: currentTheme?.theme?.darkGrey,
                    backgroundColor: currentTheme?.theme?.transparent,
                    borderWidth: 0,
                    outlineStyle: "none",
                  }}
                />
                <View
                  style={{
                    width: 1,
                    height: 24,
                    backgroundColor: currentTheme?.theme?.lightGrey,
                    marginHorizontal: 6,
                  }}
                />
                <AppDropDown
                  dropDownStyle={{
                    width: 180,
                    borderWidth: 0,
                    backgroundColor: currentTheme?.theme?.transparent,
                    height: 46,
                  }}
                  isElevated={false}
                  dropdownIconColor="transparent"
                  translateLabels={false}
                  items={[
                    {
                      name: t("settings.prompt.allAreas", "All areas"),
                      id: "all",
                    },
                    ...areas.map((area) => ({ name: area, id: area })),
                  ]}
                  propertyLabel="name"
                  propertyValue="id"
                  selectedValue={activeArea}
                  onValueChange={(itemValue) => setActiveArea(itemValue)}
                />
              </View>
              <AppText
                style={{
                  fontSize: 11,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: currentTheme?.theme?.mediumGrey,
                  marginTop: 20,
                }}
              >
                {t("settings.qa.createNew", "Create new")}
              </AppText>
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  marginRight: -10,
                }}
              >
                <CreateNewCard
                  currentTheme={currentTheme}
                  t={t}
                  onPress={() => onNewPromptSelect()}
                />
              </View>
            </View>
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ paddingBottom: 24 }}
              showsVerticalScrollIndicator
            >
              {sortedPrompts.length > 0 ? (
                <View
                  style={{
                    flexWrap: "wrap",
                    justifyContent: "flex-start",
                    flexDirection: "row",
                    marginTop: 20,
                  }}
                >
                  {sortedPrompts.map((prompt) => (
                    <PromptCard
                      key={prompt.id}
                      prompt={prompt}
                      t={t}
                      currentTheme={currentTheme}
                      isSelected={
                        showPreview && selectedPromptId === prompt?.id
                      }
                      onPress={() => handleCardPress(prompt)}
                    />
                  ))}
                </View>
              ) : (
                <View
                  style={{
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: 40,
                  }}
                >
                  <AppPlaceholderView
                    wrapperStyle={{
                      width: "80%",
                      height: 150,
                      alignItems: "center",
                    }}
                    mainCaption={t(
                      "settings.prompt.noMatchSearch",
                      "No prompts match your search"
                    )}
                    subCaption={t(
                      "settings.prompt.noMatchSearchSub",
                      "Prompt templates are currently in preview. You can create your own prompt from scratch above."
                    )}
                    size={40}
                    icon={AppAssets.icons.search}
                  />
                </View>
              )}
            </ScrollView>
          </View>
          {showPreview && (
            <PromptPreviewPanel
              prompt={selectedPrompt}
              currentTheme={currentTheme}
              t={t}
              onImport={onTemplatePromptSelect}
            />
          )}
        </View>
      )}
    </ToothFairyModal>
  );
};

export default PromptPreSelection;
